import { useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { LinkedListings } from "../LinkedListings";
import { enabledByText } from "./auto-match-runs";
import { AutoMatchSwitch } from "./AutoMatchSwitch";
import { useMatchAutoMatch } from "./use-match-auto-match";

/**
 * Der Schalter in den Match-Einstellungen. Er schaltet sofort und für sich, nicht mit "Speichern" des
 * Dialogs: sein Recht (`canSetAutoMatch`, WRITE auf discover) ist ein anderes als das für Name, Notizen
 * und Zugriff, und wer nur ihn bedienen darf, bekäme sonst keinen Speichern-Knopf.
 *
 * Er wirkt nur über eine Verknüpfung mit einer Ausschreibung (`Match.listings`). Deshalb steht er nur, wenn
 * das Match an einer hängt, die die Person sieht, schon Läufe hat oder noch eingeschaltet ist, und nennt
 * die Ausschreibungen beim Namen. So lässt er sich auch nach dem Lösen der letzten Verknüpfung ausschalten.
 * Ist er an, sieht ihn auch, wer ihn nicht bedienen darf: es laufen KI-Bewertungen im Namen
 * einer anderen Person.
 */
export function MatchAutoMatchSetting({ matchId, showLinkedListings = true }: { matchId: string; showLinkedListings?: boolean }) {
  const intl = useIntl();
  const auto = useMatchAutoMatch(matchId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (auto.loading) return null;
  if (auto.error) {
    return (
      <Frame>
        <p role="alert" className="smycles-field-meta py-3 text-ink">
          {userErrorMessage(intl, auto.error)}
        </p>
      </Frame>
    );
  }
  if (!auto.relevant) return null;
  if (!auto.canSet && !auto.autoMatch) return null;

  async function toggle(next: boolean) {
    setBusy(true);
    setError(null);
    try {
      await auto.setEnabled(next);
    } catch (e) {
      setError(userErrorMessage(intl, e));
    } finally {
      setBusy(false);
    }
  }

  const status = auto.autoMatch ? enabledByText(intl, auto.autoMatch) : null;

  return (
    <Frame>
      <div className="py-3">
        <AutoMatchSwitch
          checked={auto.autoMatch !== null}
          onChange={(next) => void toggle(next)}
          disabled={!auto.canSet || busy}
          status={status}
          hint={showLinkedListings ? (
            <FormattedMessage
              id="match.autoMatch.settingsHintListings"
              defaultMessage="{count, plural, =0 {Mit keiner Ausschreibung verknüpft, die du siehst.} one {Wirkt für die Ausschreibung {listings}.} other {Wirkt für die Ausschreibungen {listings}.}}"
              values={{ count: auto.listings.length, listings: <LinkedListings listings={auto.listings} /> }}
            />
          ) : undefined}
          error={error}
        />
      </div>
    </Frame>
  );
}

// Die flache Box wie die Zugriffsliste darunter. Ohne Beschriftung darüber: die Zeile trägt ihren Titel
// selbst, eine zweite Zeile darüber wäre eine Eyebrow.
function Frame({ children }: { children: ReactNode }) {
  return <div className="bg-page box-flat px-4">{children}</div>;
}
